import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import PageTitle from "./PageTitle";
import Spacer from "./Spacer";
import { getParticipantIdFromSearchParams } from '../utils/urlParams';
import { DEBATE_TOPIC_TITLE } from '../gameConfig';
import './DemographicsPage.styles.css';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

const questionPromptListStyle: React.CSSProperties = {
  margin: '8px 0 0 0',
  paddingLeft: '1.25rem',
  color: '#334155',
  fontWeight: 500,
  lineHeight: 1.5,
  fontSize: '0.98em',
};

const DiscussionQuestionBullets = () => (
  <>
    <ul style={questionPromptListStyle}>
      <li>What was your view?</li>
      <li>Where did you and your partner agree or disagree?</li>
      <li>Did the discussion change your thinking?</li>
    </ul>
    <span style={{ display: 'block', marginTop: 6, fontSize: '0.92em', color: '#64748b' }}>
      A few sentences.
    </span>
  </>
);

const QUESTION_TRUST = `To what extent did you trust your partner?`;
const QUESTION_FEEDBACK = `Any additional comments or feedback?`;

const LIKERT_VALUES = [1, 2, 3, 4, 5, 6, 7] as const;

const PostGameQuestionsPage: React.FC = () => {
  const [discussionReflection, setDiscussionReflection] = useState('');
  const [strategyPerception, setStrategyPerception] = useState('');
  const [partnerTrust, setPartnerTrust] = useState<number | null>(null);
  const [additionalComments, setAdditionalComments] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [debateTopicTitle, setDebateTopicTitle] = useState(DEBATE_TOPIC_TITLE);
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const participantId = getParticipantIdFromSearchParams(window.location.search);

  useEffect(() => {
    if (!participantId || !API_BASE_URL) return;
    fetch(`${API_BASE_URL}/checkPlayerStatus`, {
      method: 'POST',
      headers: { 'prolific-id': participantId },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && typeof data.debateTopic === 'string' && data.debateTopic.trim()) {
          setDebateTopicTitle(data.debateTopic.trim());
        }
      })
      .catch(() => {});
  }, [participantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!participantId) {
      setError("Participant ID missing from URL. Please use your study link (e.g. ?participantId=... or ?prolificID=...).");
      return;
    }
    if (!discussionReflection.trim()) {
      setError("Please answer question 1.");
      return;
    }
    if (!strategyPerception.trim()) {
      setError("Please answer question 2.");
      return;
    }
    if (partnerTrust == null) {
      setError("Please answer question 3.");
      return;
    }

    setSubmitting(true);
    try {
      const res1 = await fetch(`${API_BASE_URL}/postGameThemesReflection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'prolific-id': participantId,
        },
        body: JSON.stringify({ reflectionText: discussionReflection.trim() }),
      });
      if (!res1.ok) {
        const data = await res1.json().catch(() => ({}));
        const errMsg = typeof data.error === 'string' ? data.error : 'Failed to submit reflection.';
        setError(errMsg);
        setSubmitting(false);
        return;
      }

      const res2 = await fetch(`${API_BASE_URL}/postGameQuestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'prolific-id': participantId,
        },
        body: JSON.stringify({
          strategyPerception: strategyPerception.trim(),
          partnerTrust,
          additionalComments: additionalComments.trim() || undefined,
        }),
      });
      if (!res2.ok) {
        const data = await res2.json().catch(() => ({}));
        const errMsg = typeof data.error === 'string' ? data.error : 'Failed to submit post-game reflection.';
        setError(errMsg);
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      setTimeout(() => {
        navigate('/post-game-risk?' + queryParams.toString());
      }, 1000);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  const textareaStyle = {
    width: '100%' as const,
    minHeight: '120px' as const,
    padding: '12px' as const,
    border: '1.2px solid #c3d6ee' as const,
    borderRadius: '8px' as const,
    fontSize: '1em' as const,
    background: '#f7fbff' as const,
    color: '#222' as const,
    outline: 'none' as const,
    transition: 'border 0.2s, box-shadow 0.2s' as const,
    boxShadow: '0 1px 4px rgba(25, 118, 210, 0.02)' as const,
    fontFamily: 'inherit' as const,
    resize: 'vertical' as const,
  };

  const handleTextareaFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.style.border = '1.7px solid #1976d2';
    e.target.style.background = '#e3f2fd';
    e.target.style.boxShadow = '0 2px 8px rgba(25, 118, 210, 0.08)';
  };

  const handleTextareaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.style.border = '1.2px solid #c3d6ee';
    e.target.style.background = '#f7fbff';
    e.target.style.boxShadow = '0 1px 4px rgba(25, 118, 210, 0.02)';
  };

  if (submitted) {
    return (
      <div className="demographics-bg">
        <div className="demographics-card fade-in postgame-reflection-card">
          <div className="demographics-thankyou">
            Thank you! Continuing...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="demographics-bg">
      <div className="demographics-card fade-in postgame-reflection-card">
        <PageTitle title="Post-Game Reflection" />
        <Spacer />

        <form onSubmit={handleSubmit} className="demographics-form">
          {error && <div className="demographics-error">{error}</div>}

          <div className="demographics-field">
            <label>
              <p className="postgame-question-line">
                1. <strong>(Task 1)</strong>{' '}
                <span className="required">*</span>{' '}
                Reflect on the in-game discussion about <strong>&ldquo;{debateTopicTitle}&rdquo;</strong>:
              </p>
              <DiscussionQuestionBullets />
              <div style={{ marginTop: 6 }}>
                <textarea
                  value={discussionReflection}
                  onChange={(e) => setDiscussionReflection(e.target.value)}
                  required
                  placeholder="Please share your thoughts..."
                  style={textareaStyle}
                  onFocus={handleTextareaFocus}
                  onBlur={handleTextareaBlur}
                />
              </div>
            </label>
          </div>

          <div className="demographics-field">
            <label>
              <p className="postgame-question-line">
                2. <strong>(Task 2)</strong>{' '}
                <span className="required">*</span>{' '}
                What was your strategy in the investment round?
              </p>
              <div style={{ marginTop: 6 }}>
                <textarea
                  value={strategyPerception}
                  onChange={(e) => setStrategyPerception(e.target.value)}
                  required
                  placeholder="Please share your thoughts..."
                  style={textareaStyle}
                  onFocus={handleTextareaFocus}
                  onBlur={handleTextareaBlur}
                />
              </div>
            </label>
          </div>

          <div className="demographics-field">
            <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
              <legend className="demographics-label" style={{ marginBottom: 8, display: 'block', width: '100%' }}>
                3. {QUESTION_TRUST}{' '}
                <span className="required" style={{ display: 'inline' }}>*</span>
              </legend>
              <div className="postgame-likert-scale-wrap">
                <div className="postgame-likert-scale-labels">
                  <span>do not trust at all</span>
                  <span>trust completely</span>
                </div>
                <div className="postgame-likert-scale" role="radiogroup" aria-label="Partner trust rating from 1 to 7">
                  {LIKERT_VALUES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`postgame-likert-option${partnerTrust === value ? ' selected' : ''}`}
                      onClick={() => setPartnerTrust(value)}
                      disabled={submitting}
                      aria-pressed={partnerTrust === value}
                      aria-label={`${value} of 7`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>
            </fieldset>
          </div>

          <div className="demographics-field">
            <label>
              <span className="demographics-label" style={{ marginBottom: 8, display: 'block' }}>
                4. {QUESTION_FEEDBACK}
              </span>
              <div style={{ marginTop: 6 }}>
                <textarea
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                  placeholder="Optional, share anything else you'd like..."
                  style={textareaStyle}
                  onFocus={handleTextareaFocus}
                  onBlur={handleTextareaBlur}
                />
              </div>
            </label>
          </div>

          <button
            type="submit"
            className="demographics-submit"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PostGameQuestionsPage;
